/**
 * QueryBuilder Store - builder slice.
 *
 * The Builder tabs: create/remove/rename/select, per-builder query shape
 * (rows, cols, stats, op, universe, filterGroups, transpose), pipeline
 * binding, output name, backend hydration + saved-name tracking.
 */
import { StateCreator } from "zustand"
import { TABLE_OPERATION_TYPE } from "types/ReportingDashboard/elements.types"
import { BuilderTab } from "types/queryBuilder.types"
import { BuilderSlice, QueryBuilderStore } from "../types"
import {
  DEFAULT_DTAB_OPERATORS,
  DEFAULT_XTAB_OPERATORS,
  blankBuilder,
  dedupePush,
  defaultBuilderOutputName,
  newId,
  nextBuilderName,
  updateActive,
} from "../helpers"

export const createBuilderSlice: StateCreator<
  QueryBuilderStore,
  [["zustand/persist", unknown]],
  [],
  BuilderSlice
> = (set) => ({
  builders: [],
  activeBuilderId: null,
  builderSavedNames: {},

  createBuilder: () => {
    let createdId = ""
    set((state) => {
      const fresh = blankBuilder(
        nextBuilderName(state.builders),
        state.activeTransformPipelineId,
        state.builders
      )
      createdId = fresh.id
      return {
        builders: [...state.builders, fresh],
        activeBuilderId: fresh.id,
      }
    })
    return createdId
  },

  removeBuilder: (id) =>
    set((state) => {
      // Drop the saved-name binding alongside the builder itself.
      const nextSavedNames = { ...state.builderSavedNames }
      delete nextSavedNames[id]

      const remaining = state.builders.filter((b) => b.id !== id)
      // Cross-slice write: clearing the session slice's lastRun* is
      // deliberate — slices share one store state.
      const lastRunCleared =
        state.lastRunBuilderId === id
          ? { lastRunBuilderId: null, lastRunResult: null }
          : {}
      if (remaining.length === 0) {
        const fresh = blankBuilder(
          "Builder 1",
          state.activeTransformPipelineId,
          remaining
        )
        return {
          builders: [fresh],
          activeBuilderId: fresh.id,
          builderSavedNames: nextSavedNames,
          ...lastRunCleared,
        }
      }
      const nextActive =
        state.activeBuilderId === id
          ? remaining[remaining.length - 1].id
          : state.activeBuilderId
      return {
        builders: remaining,
        activeBuilderId: nextActive,
        builderSavedNames: nextSavedNames,
        ...lastRunCleared,
      }
    }),

  setActiveBuilder: (id) => set({ activeBuilderId: id }),

  renameBuilder: (id, name) =>
    set((state) => ({
      builders: state.builders.map((b) =>
        b.id === id ? { ...b, name: name.trim() || b.name } : b
      ),
    })),

  setOperation: (op) =>
    set((state) =>
      updateActive(state, (b) =>
        op === b.operation
          ? b
          : {
              ...b,
              operation: op,
              selectedOperators:
                op === TABLE_OPERATION_TYPE.XTAB
                  ? DEFAULT_XTAB_OPERATORS
                  : DEFAULT_DTAB_OPERATORS,
            }
      )
    ),
  addRow: (variable) =>
    set((state) =>
      updateActive(state, (b) => ({
        ...b,
        rowVariables: dedupePush(b.rowVariables, variable),
      }))
    ),
  removeRow: (variable) =>
    set((state) =>
      updateActive(state, (b) => ({
        ...b,
        rowVariables: b.rowVariables.filter((v) => v !== variable),
      }))
    ),
  addColumn: (variable) =>
    set((state) =>
      updateActive(state, (b) => ({
        ...b,
        columnVariables: dedupePush(b.columnVariables, variable),
      }))
    ),
  removeColumn: (variable) =>
    set((state) =>
      updateActive(state, (b) => ({
        ...b,
        columnVariables: b.columnVariables.filter((v) => v !== variable),
      }))
    ),
  toggleOperator: (op) =>
    set((state) =>
      updateActive(state, (b) => ({
        ...b,
        selectedOperators: b.selectedOperators.includes(op)
          ? b.selectedOperators.filter((o) => o !== op)
          : [...b.selectedOperators, op],
      }))
    ),
  setOperators: (ops) =>
    set((state) =>
      updateActive(state, (b) => ({ ...b, selectedOperators: ops }))
    ),
  setUniverseFilter: (filter) =>
    set((state) =>
      updateActive(state, (b) => ({ ...b, universeFilter: filter }))
    ),
  setIsTransposed: (transposed) =>
    set((state) =>
      updateActive(state, (b) => ({ ...b, isTransposed: transposed }))
    ),
  setBuilderTransformPipeline: (pipelineId) =>
    set((state) =>
      updateActive(state, (b) => ({
        ...b,
        transformPipelineId: pipelineId,
      }))
    ),
  addFilterGroup: () => {
    let createdId = ""
    set((state) => {
      if (!state.activeBuilderId) return {}
      const groupId = newId()
      createdId = groupId
      return updateActive(state, (b) => ({
        ...b,
        filterGroups: [...b.filterGroups, { id: groupId, variables: [] }],
      }))
    })
    return createdId
  },
  removeFilterGroup: (groupId) =>
    set((state) =>
      updateActive(state, (b) => ({
        ...b,
        filterGroups: b.filterGroups.filter((g) => g.id !== groupId),
      }))
    ),
  addFilterGroupVariable: (groupId, variable) =>
    set((state) =>
      updateActive(state, (b) => ({
        ...b,
        filterGroups: b.filterGroups.map((g) =>
          g.id === groupId
            ? { ...g, variables: dedupePush(g.variables, variable) }
            : g
        ),
      }))
    ),
  removeFilterGroupVariable: (groupId, variable) =>
    set((state) =>
      updateActive(state, (b) => ({
        ...b,
        filterGroups: b.filterGroups.map((g) =>
          g.id === groupId
            ? { ...g, variables: g.variables.filter((v) => v !== variable) }
            : g
        ),
      }))
    ),

  applyHydratedBuilders: (incoming) =>
    set((state) => {
      if (incoming.length === 0) return state
      // Cross-slice read: resolving against the pipeline slice's
      // transformPipelines is deliberate — slices share one store state.
      // Resolve each builder's bound pipeline by name (cross-device names
      // outlive local UUIDs). Missing pipeline → null binding (raw run).
      const pipelineIdByName = new Map(
        state.transformPipelines.map((p) => [p.name, p.id])
      )
      const byName = new Map(state.builders.map((b) => [b.name, b]))
      const updated: BuilderTab[] = []
      const handledNames = new Set<string>()
      for (const item of incoming) {
        handledNames.add(item.builder.name)
        const transformPipelineId = item.pipelineName
          ? pipelineIdByName.get(item.pipelineName) ?? null
          : null
        const existing = byName.get(item.builder.name)
        // Backend sidecars bypass `migrate`, so a pre-v10 envelope
        // arrives without `outputName`. Backfill it the same way the
        // migrate ladder would — derive from the name, dedupe against
        // already-handled entries from this hydration pass.
        const incomingOutputName = (
          item.builder as Partial<BuilderTab>
        ).outputName
        const outputName =
          incomingOutputName && incomingOutputName.length > 0
            ? incomingOutputName
            : defaultBuilderOutputName(item.builder.name, updated)
        const merged: BuilderTab = {
          ...item.builder,
          outputName,
          transformPipelineId,
          // Preserve local id when matched, else freshly assigned.
          id: existing?.id ?? newId(),
        }
        updated.push(merged)
      }
      for (const local of state.builders) {
        if (!handledNames.has(local.name)) {
          updated.push(local)
        }
      }
      const activeStillExists = updated.some(
        (b) => b.id === state.activeBuilderId
      )
      const activeBuilderId = activeStillExists
        ? state.activeBuilderId
        : updated[0]?.id ?? null
      const savedNames: Record<string, string> = {
        ...state.builderSavedNames,
      }
      updated.forEach((b) => {
        if (handledNames.has(b.name)) savedNames[b.id] = b.name
      })
      return {
        builders: updated,
        activeBuilderId,
        builderSavedNames: savedNames,
      }
    }),

  markBuilderSaved: (builderId, savedName) =>
    set((state) => ({
      builderSavedNames: {
        ...state.builderSavedNames,
        [builderId]: savedName,
      },
    })),

  resetActiveBuilder: () =>
    set((state) =>
      updateActive(state, (b) => ({
        // Preserve the output name across resets — the user picked it,
        // and a reset wiping the table id every time would re-break
        // anything downstream that referenced it.
        ...blankBuilder(b.name, b.transformPipelineId, state.builders),
        id: b.id,
        outputName: b.outputName,
      }))
    ),

  setBuilderOutputName: (id, outputName) =>
    set((state) => ({
      builders: state.builders.map((b) =>
        b.id === id ? { ...b, outputName } : b
      ),
    })),
})
